import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  X, Package, Edit, Trash2, History, Image as ImageIcon, 
  AlertTriangle, Clock, TrendingDown, TrendingUp, IndianRupee,
  Calendar, Tag, Layers, Upload, Star, Box, ShoppingCart, 
  FileText, Plus, BarChart2, Percent, Scale, Settings
} from 'lucide-react';
import { itemsAPI } from '../../services/part2-api';
import { toast } from 'react-hot-toast';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { Button } from '@/components/ui/button';
import ConfirmModal from '../modals/ConfirmModal';

// Info Row Component - Moved outside main component
const InfoRow = ({ label, value, mono = false, className = '' }) => (
  <div className={className}>
    <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
    <p className={`text-sm text-slate-700 ${mono ? 'font-mono' : ''}`}>{value || '-'}</p>
  </div>
);

const ProductDetailDrawer = ({ 
  isOpen, 
  onClose, 
  productId, 
  onEdit, 
  onDelete,
  onRefresh,
  onAdjustStock 
}) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [transactions, setTransactions] = useState({ sales: [], purchases: [], summary: {} });
  const [stockMovements, setStockMovements] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const fileInputRef = useRef(null);

  const fetchProduct = useCallback(async () => {
    if (!productId) return;
    try {
      setLoading(true);
      const response = await itemsAPI.get(productId);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Failed to load product details');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  const fetchTransactions = useCallback(async () => {
    if (!productId) return;
    try {
      setTransactionsLoading(true);
      const response = await itemsAPI.getTransactions(productId, { limit: 20 });
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  }, [productId]);

  const fetchStockMovements = useCallback(async () => {
    if (!productId) return;
    try {
      const response = await itemsAPI.getStockMovements(productId, { limit: 10 });
      setStockMovements(response.data.movements || []);
    } catch (error) {
      console.error('Failed to fetch stock movements:', error);
    }
  }, [productId]);

  useEffect(() => {
    if (isOpen && productId) {
      fetchProduct();
      setActiveTab('overview');
    }
  }, [isOpen, productId, fetchProduct]);

  useEffect(() => {
    if (activeTab === 'transactions' && productId) {
      fetchTransactions();
    } else if (activeTab === 'inventory' && productId) {
      fetchStockMovements();
    }
  }, [activeTab, productId, fetchTransactions, fetchStockMovements]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Calculate expiry status
  const getExpiryStatus = () => {
    if (!product?.expiry_date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(product.expiry_date);
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Expired', color: 'red', days: Math.abs(daysUntilExpiry) };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'expiring', label: 'Expiring Soon', color: 'red', days: daysUntilExpiry };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', label: 'Expiring Soon', color: 'amber', days: daysUntilExpiry };
    }
    return { status: 'valid', label: 'Valid', color: 'emerald', days: daysUntilExpiry };
  };

  // Calculate stock status
  const getStockStatus = () => {
    if (product?.item_type === 'service') return null;
    const stock = product?.current_stock || 0;
    const reorderLevel = product?.reorder_level || product?.stock_config?.low_stock_threshold || 10;
    
    if (stock === 0) {
      return { status: 'out', label: 'Out of Stock', color: 'red' };
    } else if (stock <= reorderLevel) {
      return { status: 'low', label: 'Low Stock', color: 'red' };
    }
    return { status: 'ok', label: 'In Stock', color: 'emerald' };
  };

  // Calculate margin
  const calculateMargin = () => {
    const sellingPrice = product?.selling_price || product?.pricing?.sales_price || 0;
    const purchasePrice = product?.purchase_price || product?.pricing?.purchase_price || 0;
    if (!sellingPrice || !purchasePrice) return null;
    const margin = ((sellingPrice - purchasePrice) / sellingPrice) * 100;
    return margin.toFixed(1);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      const isPrimary = !product?.images?.length;
      await itemsAPI.uploadImage(productId, file, isPrimary);
      toast.success('Image uploaded successfully');
      fetchProduct();
      onRefresh?.();
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error(getApiErrorMessage(error, 'Failed to upload image'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (imageUrl) => {
    setConfirmModal({
      title: 'Remove Image',
      description: 'Are you sure you want to remove this image? This action cannot be undone.',
      confirmLabel: 'Remove',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await itemsAPI.removeImage(productId, imageUrl);
          toast.success('Image removed');
          fetchProduct();
          onRefresh?.();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to remove image'));
        }
      },
    });
  };

  const handleSetPrimaryImage = async (imageUrl) => {
    try {
      await itemsAPI.setPrimaryImage(productId, imageUrl);
      toast.success('Primary image updated');
      fetchProduct();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to update primary image');
    }
  };

  if (!isOpen) return null;

  const expiryStatus = product ? getExpiryStatus() : null;
  const stockStatus = product ? getStockStatus() : null;
  const margin = product ? calculateMargin() : null;

  // Parse images - handle both array and JSON string
  let images = [];
  if (product?.images) {
    if (typeof product.images === 'string') {
      try {
        images = JSON.parse(product.images);
      } catch (e) {
        images = [];
      }
    } else if (Array.isArray(product.images)) {
      images = product.images;
    }
  }
  // Normalize image format (some may be just URLs, some may be objects)
  images = images.map(img => {
    if (typeof img === 'string') {
      return { url: img, filename: '' };
    }
    return img;
  });
  
  // If primary_image_url or image exists but not in images array, add it
  const existingPrimaryUrl = product?.primary_image_url || product?.image;
  if (existingPrimaryUrl && !images.some(img => img.url === existingPrimaryUrl)) {
    images.unshift({ url: existingPrimaryUrl, filename: 'Primary Image' });
  }
  
  const primaryImage = existingPrimaryUrl || (images.length > 0 ? images[0]?.url : null);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Package },
    { id: 'inventory', label: 'Inventory', icon: Box },
    { id: 'pricing', label: 'Pricing & Tax', icon: IndianRupee },
    { id: 'transactions', label: 'Transactions', icon: FileText },
    { id: 'media', label: 'Media', icon: ImageIcon }
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-40 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div 
        className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-out"
        data-testid="product-detail-drawer"
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-br from-slate-50 to-white border-b border-slate-200">
          {/* Top Actions Bar */}
          <div className="px-5 py-3 flex items-center justify-between">
            <button 
              onClick={onClose}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit?.(product)}
                className="text-xs h-8"
                data-testid="edit-product-btn"
              >
                <Edit className="w-3.5 h-3.5 mr-1.5" />
                Edit
              </Button>
              {product?.item_type === 'product' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-8"
                  onClick={() => {
                    onClose();
                    onAdjustStock?.(product);
                  }}
                  data-testid="adjust-stock-btn"
                >
                  <Layers className="w-3.5 h-3.5 mr-1.5" />
                  Adjust Stock
                </Button>
              )}
            </div>
          </div>

          {/* Product Header Info */}
          {!loading && product && (
            <div className="px-5 pb-4">
              <div className="flex items-start gap-4">
                {/* Product Image */}
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-teal-50 to-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0 border border-slate-200/50 shadow-sm">
                  {primaryImage ? (
                    <img 
                      src={primaryImage} 
                      alt={product.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Package className="w-7 h-7 text-teal-400" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-slate-800 truncate">{product.name}</h2>
                  <p className="text-xs text-slate-500 mt-0.5 font-mono">
                    {product.code || product.sku || 'No SKU'} 
                    {product.category && <span className="text-slate-400"> • {product.category}</span>}
                  </p>
                  
                  {/* Status Badges */}
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {/* Stock Status */}
                    {stockStatus && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${
                        stockStatus.color === 'red' ? 'bg-red-100 text-red-700' :
                        stockStatus.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {stockStatus.label}
                      </span>
                    )}
                    
                    {/* Expiry Status */}
                    {expiryStatus && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${
                        expiryStatus.color === 'red' ? 'bg-red-100 text-red-700' :
                        expiryStatus.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        <Clock className="w-2.5 h-2.5 mr-1" />
                        {expiryStatus.status === 'expired' 
                          ? `Expired ${expiryStatus.days}d ago`
                          : `${expiryStatus.days}d left`
                        }
                      </span>
                    )}
                    
                    {/* Item Type */}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${
                      product.item_type === 'service' ? 'bg-violet-100 text-violet-700' : 'bg-teal-100 text-teal-700'
                    }`}>
                      {product.item_type === 'service' ? 'Service' : 'Product'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex items-center gap-0.5 px-5 overflow-x-auto scrollbar-none">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap rounded-t-lg transition-all ${
                    activeTab === tab.id
                      ? 'text-teal-700 bg-white border-t border-x border-slate-200 -mb-px relative z-10'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100/50'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-slate-50/50">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-3">
                <div className="w-7 h-7 border-2 border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-xs text-slate-500">Loading...</p>
              </div>
            </div>
          ) : product ? (
            <div className="p-5 space-y-4">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <>
                  {/* Basic Info Card */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50">
                      <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Basic Information</h3>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                        <InfoRow label="Item Name" value={product.name} />
                        <InfoRow label="SKU / Item Code" value={product.code || product.sku} mono />
                        <InfoRow label="Barcode" value={product.barcode} mono />
                        <InfoRow label="Category" value={product.category} />
                        <InfoRow label="Brand" value={product.brand} />
                        <InfoRow label="Unit" value={product.primary_unit || product.unit || 'PCS'} />
                        <InfoRow label="HSN/SAC" value={product.hsn} mono />
                        <InfoRow label="Tax Rate" value={product.tax_rate ? `${product.tax_rate}%` : '-'} />
                      </div>
                      {product.description && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <InfoRow label="Description" value={product.description} />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded-xl border border-slate-200 p-3 text-center shadow-sm">
                      <p className="text-[10px] text-teal-600 uppercase tracking-wider mb-0.5">Selling</p>
                      <p className="text-lg font-semibold text-teal-700">
                        {formatCurrency(product.selling_price || product.pricing?.sales_price)}
                      </p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-3 text-center shadow-sm">
                      <p className="text-[10px] text-blue-600 uppercase tracking-wider mb-0.5">Purchase</p>
                      <p className="text-lg font-semibold text-blue-700">
                        {formatCurrency(product.purchase_price || product.pricing?.purchase_price)}
                      </p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-3 text-center shadow-sm">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Margin</p>
                      <p className={`text-lg font-semibold ${margin && parseFloat(margin) > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {margin ? `${margin}%` : '-'}
                      </p>
                    </div>
                  </div>

                  {/* Expiry Card */}
                  {product.expiry_date && (
                    <div className={`bg-white rounded-xl border overflow-hidden shadow-sm ${
                      expiryStatus?.color === 'red' ? 'border-red-200 bg-red-50/30' : 'border-slate-200'
                    }`}>
                      <div className={`px-4 py-2.5 border-b ${
                        expiryStatus?.color === 'red' ? 'bg-red-50 border-red-100' : 'bg-slate-50/50 border-slate-100'
                      }`}>
                        <h3 className={`text-xs font-semibold uppercase tracking-wider ${
                          expiryStatus?.color === 'red' ? 'text-red-700' : 'text-slate-600'
                        }`}>Expiry Information</h3>
                      </div>
                      <div className="p-4 flex items-center gap-4">
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center ${
                          expiryStatus?.color === 'red' ? 'bg-red-100' :
                          expiryStatus?.color === 'amber' ? 'bg-amber-100' :
                          'bg-emerald-100'
                        }`}>
                          <Calendar className={`w-5 h-5 ${
                            expiryStatus?.color === 'red' ? 'text-red-600' :
                            expiryStatus?.color === 'amber' ? 'text-amber-600' :
                            'text-emerald-600'
                          }`} />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Expiry Date</p>
                          <p className={`text-base font-semibold ${
                            expiryStatus?.color === 'red' ? 'text-red-700' : 'text-slate-800'
                          }`}>
                            {formatDate(product.expiry_date)}
                          </p>
                          {expiryStatus && (
                            <p className={`text-[10px] mt-0.5 ${
                              expiryStatus.color === 'red' ? 'text-red-600' :
                              expiryStatus.color === 'amber' ? 'text-amber-600' :
                              'text-emerald-600'
                            }`}>
                              {expiryStatus.status === 'expired' 
                                ? `Expired ${expiryStatus.days} days ago`
                                : `${expiryStatus.days} days remaining`
                              }
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Inventory Tab */}
              {activeTab === 'inventory' && (
                <>
                  {/* Stock Summary */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className={`bg-white rounded-xl border p-3 text-center shadow-sm ${
                      stockStatus?.color === 'red' ? 'border-red-200 bg-red-50/30' :
                      stockStatus?.color === 'amber' ? 'border-amber-200 bg-amber-50/30' :
                      'border-emerald-200 bg-emerald-50/30'
                    }`}>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Current Stock</p>
                      <p className={`text-xl font-bold ${
                        stockStatus?.color === 'red' ? 'text-red-700' :
                        stockStatus?.color === 'amber' ? 'text-amber-700' :
                        'text-emerald-700'
                      }`}>
                        {product.current_stock || 0}
                      </p>
                      <p className="text-[10px] text-slate-400">{product.primary_unit || 'PCS'}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-3 text-center shadow-sm">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Reorder Level</p>
                      <p className="text-xl font-bold text-slate-700">
                        {product.reorder_level || product.stock_config?.low_stock_threshold || '-'}
                      </p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-3 text-center shadow-sm">
                      <p className="text-[10px] text-teal-600 uppercase tracking-wider mb-0.5">Stock Value</p>
                      <p className="text-xl font-bold text-teal-700">
                        {formatCurrency((product.current_stock || 0) * (product.purchase_price || product.pricing?.purchase_price || 0))}
                      </p>
                    </div>
                  </div>

                  {/* Stock Details */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50">
                      <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Stock Details</h3>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                        <InfoRow label="Opening Stock" value={product.stock_config?.opening_stock || '0'} />
                        <InfoRow label="Stock As Of" value={formatDate(product.stock_config?.stock_as_of_date)} />
                        <InfoRow label="Low Stock Warning" value={product.stock_config?.enable_low_stock_warning ? 'Enabled' : 'Disabled'} />
                        <InfoRow label="Threshold" value={product.stock_config?.low_stock_threshold || '-'} />
                      </div>
                    </div>
                  </div>

                  {/* Stock Movements */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50">
                      <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Recent Stock Movements</h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {stockMovements.length === 0 ? (
                        <div className="p-6 text-center">
                          <History className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-xs text-slate-500">No stock movements recorded</p>
                        </div>
                      ) : (
                        stockMovements.map((movement, index) => (
                          <div key={index} className="px-4 py-2.5 flex items-center justify-between hover:bg-slate-50/50">
                            <div className="flex items-center gap-3">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                movement.type === 'IN' || movement.type === 'ADD' ? 'bg-emerald-100' : 'bg-red-100'
                              }`}>
                                {movement.type === 'IN' || movement.type === 'ADD' ? (
                                  <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                                ) : (
                                  <TrendingDown className="w-3.5 h-3.5 text-red-600" />
                                )}
                              </div>
                              <div>
                                <p className="text-xs font-medium text-slate-700">{movement.reason || movement.type}</p>
                                <p className="text-[10px] text-slate-400">{formatDate(movement.created_at)}</p>
                              </div>
                            </div>
                            <span className={`text-xs font-semibold ${
                              movement.type === 'IN' || movement.type === 'ADD' ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                              {movement.type === 'IN' || movement.type === 'ADD' ? '+' : '-'}{movement.quantity}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Pricing & Tax Tab */}
              {activeTab === 'pricing' && (
                <>
                  {/* Sales Pricing */}
                  <div className="bg-white rounded-xl border border-teal-200 overflow-hidden shadow-sm">
                    <div className="px-4 py-2.5 border-b border-teal-100 bg-teal-50/50">
                      <h3 className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Sales Pricing</h3>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Selling Price</p>
                          <p className="text-xl font-bold text-teal-600">
                            {formatCurrency(product.selling_price || product.pricing?.sales_price)}
                          </p>
                        </div>
                        <InfoRow label="Price Type" value={product.pricing?.price_type === 'inclusive' ? 'Inclusive of Tax' : 'Exclusive of Tax'} />
                        <InfoRow label="Discount (%)" value={product.pricing?.discount_percent ? `${product.pricing.discount_percent}%` : '-'} />
                        <InfoRow label="Discount (₹)" value={product.pricing?.discount_amount ? formatCurrency(product.pricing.discount_amount) : '-'} />
                        <InfoRow label="Tax Category" value={product.pricing?.sales_tax_category || '-'} />
                        <InfoRow label="Tax Rate" value={product.tax_rate ? `${product.tax_rate}%` : '-'} />
                      </div>
                    </div>
                  </div>

                  {/* Purchase Pricing */}
                  <div className="bg-white rounded-xl border border-blue-200 overflow-hidden shadow-sm">
                    <div className="px-4 py-2.5 border-b border-blue-100 bg-blue-50/50">
                      <h3 className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Purchase Pricing</h3>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Purchase Price</p>
                          <p className="text-xl font-bold text-blue-600">
                            {formatCurrency(product.purchase_price || product.pricing?.purchase_price)}
                          </p>
                        </div>
                        <InfoRow label="Tax Category" value={product.pricing?.purchase_tax_category || '-'} />
                      </div>
                    </div>
                  </div>

                  {/* Margin Analysis */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50">
                      <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Margin Analysis</h3>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                          margin && parseFloat(margin) > 20 ? 'bg-emerald-100' :
                          margin && parseFloat(margin) > 0 ? 'bg-amber-100' :
                          'bg-slate-100'
                        }`}>
                          <Percent className={`w-7 h-7 ${
                            margin && parseFloat(margin) > 20 ? 'text-emerald-600' :
                            margin && parseFloat(margin) > 0 ? 'text-amber-600' :
                            'text-slate-400'
                          }`} />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Profit Margin</p>
                          <p className={`text-2xl font-bold ${
                            margin && parseFloat(margin) > 20 ? 'text-emerald-600' :
                            margin && parseFloat(margin) > 0 ? 'text-amber-600' :
                            'text-slate-400'
                          }`}>
                            {margin ? `${margin}%` : 'N/A'}
                          </p>
                          {margin && (
                            <p className="text-[10px] text-slate-400 mt-0.5">
                              Profit per unit: {formatCurrency((product.selling_price || product.pricing?.sales_price || 0) - (product.purchase_price || product.pricing?.purchase_price || 0))}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Transactions Tab */}
              {activeTab === 'transactions' && (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-xl border border-emerald-200 p-4 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase">Total Sold</p>
                          <p className="text-lg font-bold text-emerald-700">
                            {transactions.summary?.total_sold || 0}
                            <span className="text-xs font-normal text-slate-400 ml-1">{product.primary_unit || 'PCS'}</span>
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {transactions.summary?.sales_count || 0} invoices
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-blue-200 p-4 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                          <ShoppingCart className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase">Total Purchased</p>
                          <p className="text-lg font-bold text-blue-700">
                            {transactions.summary?.total_purchased || 0}
                            <span className="text-xs font-normal text-slate-400 ml-1">{product.primary_unit || 'PCS'}</span>
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {transactions.summary?.purchase_count || 0} bills
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Sales */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="px-4 py-2.5 border-b border-emerald-100 bg-emerald-50/50">
                      <h3 className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Recent Sales</h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {transactionsLoading ? (
                        <div className="p-6 text-center">
                          <div className="w-5 h-5 border-2 border-slate-200 border-t-emerald-600 rounded-full animate-spin mx-auto"></div>
                        </div>
                      ) : transactions.sales?.length === 0 ? (
                        <div className="p-6 text-center">
                          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-xs text-slate-500">No sales transactions</p>
                        </div>
                      ) : (
                        transactions.sales?.slice(0, 5).map((txn, index) => (
                          <div key={index} className="px-4 py-2.5 flex items-center justify-between hover:bg-slate-50/50">
                            <div>
                              <p className="text-xs font-medium text-slate-700">{txn.document_number}</p>
                              <p className="text-[10px] text-slate-400">{txn.party_name || 'Walk-in'} • {formatDate(txn.date)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-medium text-slate-700">{txn.quantity} {product.primary_unit || 'PCS'}</p>
                              <p className="text-[10px] text-emerald-600 font-medium">{formatCurrency(txn.line_total)}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Recent Purchases */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="px-4 py-2.5 border-b border-blue-100 bg-blue-50/50">
                      <h3 className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Recent Purchases</h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {transactionsLoading ? (
                        <div className="p-6 text-center">
                          <div className="w-5 h-5 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                        </div>
                      ) : transactions.purchases?.length === 0 ? (
                        <div className="p-6 text-center">
                          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-xs text-slate-500">No purchase transactions</p>
                        </div>
                      ) : (
                        transactions.purchases?.slice(0, 5).map((txn, index) => (
                          <div key={index} className="px-4 py-2.5 flex items-center justify-between hover:bg-slate-50/50">
                            <div>
                              <p className="text-xs font-medium text-slate-700">{txn.document_number}</p>
                              <p className="text-[10px] text-slate-400">{txn.party_name || 'Unknown'} • {formatDate(txn.date)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-medium text-slate-700">{txn.quantity} {product.primary_unit || 'PCS'}</p>
                              <p className="text-[10px] text-blue-600 font-medium">{formatCurrency(txn.line_total)}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Media Tab */}
              {activeTab === 'media' && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Product Images</h3>
                    <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium cursor-pointer transition-colors ${
                      uploadingImage 
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}>
                      {uploadingImage ? (
                        <>
                          <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-3 h-3" />
                          Upload
                        </>
                      )}
                      <input 
                        ref={fileInputRef}
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                    </label>
                  </div>
                  <div className="p-4">
                    {images.length === 0 ? (
                      <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
                        <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-xs text-slate-500 mb-3">No images uploaded</p>
                        <label className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-xs font-medium cursor-pointer">
                          <Plus className="w-3.5 h-3.5" />
                          Add First Image
                          <input 
                            type="file" 
                            accept="image/*" 
                            className="hidden" 
                            onChange={handleImageUpload}
                            disabled={uploadingImage}
                          />
                        </label>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-3">
                        {images.map((img, index) => (
                          <div 
                            key={index} 
                            className={`relative group rounded-lg overflow-hidden aspect-square border-2 transition-all ${
                              img.url === primaryImage ? 'border-teal-500 ring-2 ring-teal-100' : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <img 
                              src={img.url} 
                              alt={img.filename || `Product ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            {img.url === primaryImage && (
                              <div className="absolute top-1.5 left-1.5 bg-teal-600 text-white px-1.5 py-0.5 rounded text-[9px] font-medium flex items-center gap-0.5">
                                <Star className="w-2.5 h-2.5" />
                                Primary
                              </div>
                            )}
                            {/* Always visible delete button in top-right corner */}
                            <button
                              onClick={() => handleRemoveImage(img.url)}
                              className="absolute top-1.5 right-1.5 p-1.5 bg-red-500 hover:bg-red-600 rounded-full text-white shadow-md transition-colors"
                              title="Delete image"
                              data-testid={`delete-image-${index}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            {/* Hover overlay for Set Primary action */}
                            {img.url !== primaryImage && (
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <button
                                  onClick={() => handleSetPrimaryImage(img.url)}
                                  className="p-2 bg-white rounded-lg text-teal-600 hover:bg-teal-50 transition-colors flex items-center gap-1.5 text-xs font-medium"
                                  title="Set as primary"
                                >
                                  <Star className="w-4 h-4" />
                                  Set Primary
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <p className="text-sm text-slate-500">Product not found</p>
            </div>
          )}
        </div>
      </div>
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </>
  );
};

export default ProductDetailDrawer;
